// # 대화 화면을 데이터에 잇는 곳
//
// `@maru/ui` 의 `Workspace` 는 props 만 받는다. 그 props 를 채우는 일이 여기 있다:
// 채널 목록은 TanStack Query, 뱃지·커서는 zustand 저장소, 본문은 타임라인 캐시,
// 쓰기는 세션의 `api` 다.
//
// ## 안 읽음 줄은 **열 때의 값으로 얼어붙는다**
// 채널을 열면 곧바로 읽음이 올라간다. 그때 `read_upto` 를 그대로 보면 띠가 즉시
// 사라져서 "뭐가 새로 왔는지" 를 볼 틈이 없다. 그래서 연 순간의 값을 붙들어 두고,
// 읽음 표시는 서버로만 올린다 — 눈에 보이는 것과 서버의 진실이 각자 맞다.

import type {
  Channel,
  ChannelPref,
  ConnectLink,
  Marked,
  MaruApi,
  Member,
  Message,
  ProfileRow,
  Session,
  ThreadBrief,
} from '@maru/client'
import { ApiError, chat_store, of_file, stale_queries, upload_file } from '@maru/client'
import type { SearchNames } from '@maru/client/search_query'
import type { Platform } from '@maru/platform'
import {
  user as as_user,
  attachments_of,
  type Block,
  body_of,
  type ChannelId,
  type Event,
  fresh_msg_id,
  type GroupId,
  links_of,
  type Seq,
  text_of,
  type UserId,
} from '@maru/protocol'
import {
  type Brief,
  type FileHit,
  fmt_when_in,
  type Hit,
  hidden_after_close,
  locale_now,
  type Manage,
  type Mark,
  mark_keywords,
  msg,
  type NotifyScope,
  type OrgHands,
  type Person,
  type SelfView,
  type SettingsTab,
  type StartClip,
  type SummaryHow,
  step_channel,
  type ThreadView,
  type ToastStore,
  translate,
  trouble,
  type UnreadGroup,
  useT,
  useToasts,
  type WhisperItem,
  Workspace,
  why,
} from '@maru/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  lazy,
  Suspense,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import {
  NEWS_PEEK,
  newest,
  news_count,
  read_seen,
  seen_key,
  starter_key,
  write_seen,
} from './activity_news.ts'
import { open_canvas } from './canvas.ts'
import { DraftSync } from './draft_sync.ts'
import { DraftBook, thread_draft } from './drafts.ts'
import { useHuddleHands } from './huddle.ts'
import { load_notify, wants_alert } from './notify_scope.ts'
import { copy_text, keep_open, last_open, link_to, put_hash, read_link } from './permalink.ts'
import { command_candidates, from_key, near_shortcuts, split_shortcuts } from './shortcuts.ts'
import { useSidebarView } from './sidebar_view.ts'
import { type Modal, patched, placed, popped, type Stack } from './view_stack.ts'

/** 이 기기에 녹음기가 있는가. 없으면 🎙·📹 단추를 안 그린다. */
function can_record(): boolean {
  return (
    typeof globalThis.MediaRecorder === 'function' &&
    typeof globalThis.navigator?.mediaDevices?.getUserMedia === 'function'
  )
}

/** 녹음기(`clip.ts`)는 🎙·📹 를 누를 때 받는다 — 누르기 전엔 첫 화면에 실을 까닭이 없다. */
const start_clip: StartClip = (kind, full) =>
  import('./clip.ts').then((m) => m.start_clip(kind, full))

/**
 * 알림음(`chime.ts`)은 울릴 때 받는다. 모듈 수준에 두는 것이 요점이다 — 컴포넌트 안의 `import()` 는
 * React Compiler 가 그 컴포넌트의 메모를 통째로 포기하게 한다(`Room` 의 캐시 칸 603 → 168).
 */
const ring = () => void import('./chime.ts').then((m) => m.chime())

/**
 * 초록불은 이 간격으로 **묻는다**. 밀어 주는 사건이 아니다 — 명단이 크면 접속 변화가
 * 메시지보다 잦아서, 밀면 소켓이 그것으로 찬다.
 */
// 앱이 연 창은 앱을 쓰는 조직에서도 드물다. 첫 청크에 싣지 않고 처음 창이 뜰 때 받는다.
const ViewModal = lazy(() => import('@maru/ui/view_modal').then((m) => ({ default: m.ViewModal })))

const PRESENCE_EVERY_MS = 30_000
/** 첨부 서명(45초 수명)을 다시 받는 간격. 만료 안 된 것은 `need` 가 건너뛴다. */
const FILE_REFRESH_MS = 15_000

/**
 * 반응 모음에 "나" 를 알려 준다. 렌더 안에서 부르지만 값이 같으면 건드리지 않는다 —
 * 컴포넌트 몸에서 바로 대입하면 React Compiler 가 그 컴포넌트를 통째로 건너뛴다.
 */
/** 공지 채널에 새 글을 쓸 역할인가. 명단이 아직 없으면 못 쓰는 쪽으로 본다. */
/** 검색 수정자의 이름 → 번호. 표시 이름의 띄어쓰기는 없애고 대소문자는 가리지 않는다. */
function search_names(
  people: readonly { id: UserId; display_name: string }[] | undefined,
  rooms: readonly { id: ChannelId; name: string | null }[] | undefined,
): SearchNames {
  const key = (v: string) => v.replaceAll(/\s+/g, '').toLowerCase()
  return {
    user: (n) => people?.find((p) => key(p.display_name) === key(n))?.id,
    channel: (n) => rooms?.find((c) => c.name !== null && key(c.name) === key(n))?.id,
  }
}

/**
 * 검색어 풀이는 검색할 때만 받는다 — 첫 화면에 싣지 않는다(docs/design2.md E35). 컴포넌트
 * 밖에 둔다: 안에서 `import()` 를 부르면 React Compiler 가 `Room` 전체의 기억을 포기한다.
 */
async function parse_query(text: string, names: SearchNames) {
  return (await import('@maru/client/search_query')).parse_search(text, names)
}

function may_announce(people: readonly { id: number; role: string }[] | undefined, me: number) {
  const role = people?.find((m) => m.id === me)?.role
  return role === 'admin' || role === 'owner'
}

// zustand 의 훅은 `use` 로 시작해야 React Compiler 가 훅으로 안다. `chat_store(...)` 를 그대로
// 부르면 컴파일러가 순수 호출로 보고 캐시 뒤에 숨겨 두 번째 렌더부터 건너뛴다 — 훅 순서가
// 어긋나 방 화면이 통째로 하얘졌다(protocol 의 hook_names.test.ts 가 막는다).
const useChat = chat_store

function adopt_me(reactions: Session['reactions'], me: ReturnType<typeof as_user>) {
  if (reactions.me !== me) reactions.me = me
}

/** 글 뒤에 첨부 블록을 붙인다 — 보내기·예약·답글이 같은 모양이다. */
function with_files(blocks: Block[], attached: readonly { id: string }[]): Block[] {
  return [...blocks, ...attached.map((a) => ({ t: 'attachment' as const, id: a.id }))]
}

export function Room({
  session,
  me,
  platform,
  settings,
  sign_out,
  start,
  org,
}: {
  session: Session
  me: number
  platform?: Platform
  /** 사이드바 맨 위 워크스페이스 이름과 전환. 조직 목록을 모르면 없다(`orgs.ts`). */
  org?: OrgHands | undefined
  /** 설정 화면 열기(탭을 주면 그 탭으로). 문(Gate)이 준다. */
  settings?(tab?: SettingsTab, aim?: string): void
  /** 이 기기에서 로그아웃. 문(Gate)이 준다 — 자리 비우기와 캐시 비우기의 순서를 문이 안다. */
  sign_out?(): Promise<void>
  /** 처음 열 채널. 공유 시트로 들어와 고른 채널이 여기로 온다. */
  start?: ChannelId | null
}) {
  const summaries = useChat((s) => s.channels)
  const read_bump = useChat((s) => s.read_bump)
  const read_set = useChat((s) => s.read_set)
  const read_all = useChat((s) => s.read_all)
  // 이 탭이 보던 곳이 먼저다(sessionStorage) — 기기 전체의 마지막 채널(localStorage)은 새 탭의 시작점일 뿐이다.
  // 하나로만 적으면 옆 탭에서 연 채널로 이 탭이 설정을 닫거나 새로 고칠 때마다 옮겨 간다(3차 F254).
  const [open, set_open] = useState<ChannelId | null>(
    () =>
      start ?? last_open(globalThis.sessionStorage, me) ?? last_open(globalThis.localStorage, me),
  )
  // 앱이 나에게만 보인 글. 서버에 없으니 여기 말고는 둘 곳이 없고, 새로 고치면 사라진다.
  const [whispers, set_whispers] = useState<readonly Whisper[]>([])
  // 앱이 내 앞에 연 창. 한 사람 앞에는 하나뿐이다(서버도 그렇다).
  const [modals, set_modals] = useState<Stack>([])
  const { timeline, api, reactions, polls, files, cards } = session
  adopt_me(reactions, as_user(me))
  const queries = useQueryClient()
  const t = useT()
  const toasts = useToasts()
  /** 보관하며 띄운 되돌리기 알림 — 둘러보기에서 되살리면 닫는다(3차 F187). */
  const stow_toast = useRef<number | null>(null)

  // 초안첩은 이 사람 것이다 — 한 기기를 나눠 쓰면 남의 쓰다 만 글이 보이면 안 된다.
  // 서버(`/v1/drafts`)와도 잇는다 — 내 다른 기기에서 쓰다 만 글이 여기 뜬다(`draft_sync.ts`).
  const [sync] = useState(
    () => new DraftSync(new DraftBook(globalThis.localStorage, `maru.drafts.${me}`), api),
  )
  const book = sync.book
  // 이 기기에 띄울 알림의 범위와 소리(F13). 설정은 Room 을 내리고 열리므로 뜰 때 한 번 읽으면 된다.
  const [notify, set_notify] = useState<{ scope: NotifyScope; sound: boolean }>({
    scope: 'mentions',
    sound: true,
  })
  useEffect(() => {
    if (platform === undefined) return
    let alive = true
    void load_notify(platform.keep).then((v) => alive && set_notify(v))
    return () => {
      alive = false
    }
  }, [platform])
  // 사이드바 보기(G25) — 설정의 사이드바 탭과 같은 값을 나눠 쓴다.
  const [sidebar_view, set_sidebar_view] = useSidebarView(platform?.keep)
  // 이미 띄운 줄. 렌더에 쓰지 않는 기억이라 상태로 한 번만 만든다.
  const [announced] = useState(() => new Set<string>())
  // 최근 활동 한 움큼 — 사이드바 '활동' 배지만 쓴다(F58). 멘션·답글 사건이 오면 다시 받고, 반응처럼
  // 나에게 따로 오지 않는 것은 1분마다 줍는다(창이 안 보이면 멈춘다).
  const peek = useQuery({
    queryKey: ['activity-peek'],
    queryFn: () => api.activity(undefined, NEWS_PEEK),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
  const [seen, set_seen] = useState(() => read_seen(globalThis.localStorage, seen_key(me)))
  const latest = newest(peek.data ?? [])
  // 처음 켠 기기는 지금까지를 본 것으로 적는다 — 그래야 다음에 오는 것부터 센다.
  useEffect(() => {
    if (seen !== null || latest === null) return
    write_seen(globalThis.localStorage, seen_key(me), latest)
    set_seen(latest)
  }, [seen, latest, me])
  const activity_news = {
    count: news_count(peek.data ?? [], seen),
    stamp: latest ?? '',
    saw: () => {
      if (latest === null || latest === seen) return
      write_seen(globalThis.localStorage, seen_key(me), latest)
      set_seen(latest)
    },
  }
  // 찾아보기 목록의 세대. 채널이 비공개가 되면 올려 열린 판이 다시 받게 한다.
  const [browse_epoch, set_browse_epoch] = useState(0)
  const [drafts, set_drafts] = useState(() => book.channels())
  const on_draft = (channel: ChannelId, text: string) => {
    sync.edit(channel, text)
    // 글자마다 불린다. 연필이 붙고 떨어질 때만 바꾼다 — 새 `Set` 을 매번 심으면 판 전체와
    // 사이드바가 글자마다 다시 그려진다.
    const next = book.channels()
    set_drafts((was) => (was.size === next.size && [...was].every((c) => next.has(c)) ? was : next))
  }

  // 처음 한 번 받고, 내 다른 기기가 바꿨다는 사건이 오면 다시 받는다. 받은 뒤엔 연필을 다시 센다.
  useEffect(() => {
    sync.on_change(() => {
      const next = book.channels()
      set_drafts((was) =>
        was.size === next.size && [...was].every((c) => next.has(c)) ? was : next,
      )
    })
    void sync.pull()
    const off = session.on((e: Event) => sync.on_event(e))
    return () => {
      off()
      void sync.flush()
    }
  }, [sync, book, session])

  const prefs = useQuery({
    queryKey: ['channel_prefs'],
    queryFn: () => api.my_prefs(),
  })
  const sections = useQuery({
    queryKey: ['sections'],
    queryFn: () => api.sections(),
  })
  // 지금 채널의 핀과 내가 담아 둔 줄. 줄마다 표시를 그려야 해서 열린 채널 것만 들고 있는다.
  // AI 요약·답을 이 조직에서 쓸 수 있는가(서버 ∧ 조직 스위치). 스위치는 드물게 바뀌므로
  // 10분 믿는다 — 그 사이 꺼졌으면 서버가 409 `ai_off` 로 막는다. 내가 바꾸면 곧바로 다시 받는다.
  const ai = useQuery({
    queryKey: ['ai'],
    queryFn: () => api.ai_status(),
    staleTime: 10 * 60_000,
  })
  const ai_on = ai.data?.enabled === true
  // 클립 받아쓰기 — 서버에 음성 인식 공급자가 있을 때만 켠다. 없으면 `null`(녹음 판에 안내가 선다),
  // 아직 모르거나 묻지 못했으면 `undefined`(아무것도 안 그린다). 대체 경로(브라우저 음성 인식)는 없다.
  const stt = useQuery({
    queryKey: ['stt'],
    queryFn: () => api.stt_status(),
    staleTime: 10 * 60_000,
  })
  const transcribe =
    stt.data === undefined
      ? undefined
      : stt.data.enabled
        ? async (clip: File) => (await api.transcribe(clip)).text
        : null
  const pins = useQuery({
    queryKey: ['pins', open],
    queryFn: () => api.pins(open as ChannelId),
    enabled: open !== null,
  })
  const saved = useQuery({
    queryKey: ['saved'],
    queryFn: () => api.saved_items(),
  })
  const pinned_seqs = new Set((pins.data ?? []).map((m) => m.message.seq))
  // 줄의 "나중에 보기에 저장됨" 은 진행 중인 것만이다. 끝냈거나 보관한 줄에 그 표시가 남으면 할 일이 아직
  // 남은 것처럼 읽힌다(3차 F308). 그런 줄에서 다시 담으면 새로 담는 대신 진행 중으로 되돌린다.
  const [saved_seqs, shelved_seqs] = (() => {
    const here = (saved.data ?? []).filter((m) => m.channel === open)
    const live = (m: (typeof here)[number]) => (m.later?.state ?? 'in_progress') === 'in_progress'
    return [
      new Set(here.filter(live).map((m) => m.message.seq)),
      new Set(here.filter((m) => !live(m)).map((m) => m.message.seq)),
    ] as const
  })()
  const mark = (kind: 'pin' | 'save', channel: ChannelId, seq: Seq, on: boolean) => {
    const work = async () => {
      if (kind === 'pin') {
        await api.pin(channel, seq, on)
        await queries.invalidateQueries({ queryKey: ['pins', channel] })
        return
      }
      if (on && channel === open && shelved_seqs.has(seq)) {
        await api.later_item(channel, seq, { state: 'in_progress' })
      } else {
        await api.save_item(channel, seq, on)
      }
      await queries.invalidateQueries({ queryKey: ['saved'] })
    }
    const [ok, fail] =
      kind === 'pin'
        ? on
          ? [t('메시지를 채널에 고정했어요'), t('메시지를 고정하지 못했어요')]
          : [t('고정을 해제했어요'), t('고정을 해제하지 못했어요')]
        : on
          ? [t('나중에 보기에 저장했어요'), t('나중에 보기에 저장하지 못했어요')]
          : [t('나중에 보기에서 뺐어요'), t('나중에 보기에서 빼지 못했어요')]
    return told(toasts, work(), ok, fail)
  }

  // 예약 판이 다시 받을 때. 판은 제 상태로 목록을 들어서 쿼리 무효화로는 안 움직인다(3차 F154).
  const [booked, set_booked] = useState(0)
  const [marks_rev, set_marks_rev] = useState(0)
  const channels = useQuery({
    queryKey: ['channels'],
    queryFn: () => api.my_channels(),
  })
  // 닫아 둔 대화방은 목록의 `closed_upto` 로 안다. 새 글이 오면 요약의 last 가 넘어서므로
  // 다시 묻지 않아도 다시 보인다.

  // 커맨드 명단은 조직에 하나다(채널과 무관). 자주 바뀌지 않아 한참 묵혀 둔다.
  // `/` 를 치기 전에는 받지 않는다 — 첫 화면의 요청 하나를 아낀다(J10). 보낼 때 명단이
  // 아직이면 작성칸이 `load_commands` 를 기다린다.
  const [commands_on, set_commands_on] = useState(false)
  const commands_q = useQuery({
    queryKey: ['commands'],
    queryFn: () => api.commands(),
    staleTime: 5 * 60_000,
    enabled: commands_on,
  })
  const commands = command_candidates(commands_q.data ?? [])
  const load_commands = async () => {
    set_commands_on(true)
    const list = await queries.fetchQuery({
      queryKey: ['commands'],
      queryFn: () => api.commands(),
      staleTime: 5 * 60_000,
    })
    return command_candidates(list)
  }
  // 단축은 누를 곳이 있는 앱의 것만 온다. 앱을 새로 달면 몇 분 뒤에 뜬다 — 메뉴는 급하지 않다.
  // ⚡ 가 뜨는 자리(작성칸·글 줄)에 손이 처음 닿을 때 받는다(J10).
  const [shortcuts_on, set_shortcuts_on] = useState(false)
  useEffect(() => {
    if (shortcuts_on) return
    const near = (e: globalThis.Event) => {
      if (near_shortcuts(e.target)) set_shortcuts_on(true)
    }
    globalThis.addEventListener?.('pointerover', near)
    globalThis.addEventListener?.('focusin', near)
    return () => {
      globalThis.removeEventListener?.('pointerover', near)
      globalThis.removeEventListener?.('focusin', near)
    }
  }, [shortcuts_on])
  const shortcuts_q = useQuery({
    queryKey: ['shortcuts'],
    queryFn: () => api.shortcuts(),
    staleTime: 5 * 60_000,
    enabled: shortcuts_on,
  })
  const shortcut_menu = split_shortcuts(shortcuts_q.data ?? [])
  const run = async (name: string, text: string) => {
    if (open === null) return { text: '' }
    const reply = await api.command_run(open, name, text)
    if (reply.remind_at !== undefined) {
      // 리마인더는 나와의 대화방에 선다. 처음이면 그 방이 방금 생겼다 — 세션에 심어야 때가 되어
      // 온 글이 구멍으로 보이지 않고, 목록에 있어야 예약 판이 `#5` 대신 이름을 안다(3차 F149).
      const known = new Set((channels.data ?? []).map((c) => c.id))
      await queries.invalidateQueries({ queryKey: ['channels'] })
      for (const c of queries.getQueryData<Channel[]>(['channels']) ?? [])
        if (!known.has(c.id)) session.adopt(c)
      set_booked((n) => n + 1)
    }
    // `/dnd` 는 프로필의 알림 멈춤을 바꾼다 — 머리의 🔕 표시가 따라오게 다시 받는다.
    if (reply.snooze !== undefined) await queries.invalidateQueries({ queryKey: ['profiles'] })
    return reply
  }

  // 사용자 그룹(`@팀이름`). 조직에 하나이고 자주 안 바뀐다 — 커맨드와 같은 결.
  const groups_q = useQuery({
    queryKey: ['groups'],
    queryFn: () => api.groups(),
    staleTime: 5 * 60_000,
  })
  const groups = (groups_q.data ?? []).map((g) => ({
    id: `group-${g.id}`,
    name: g.handle,
    hint: g.note ?? g.name,
  }))

  // 커스텀 이모지. 조직에 하나이고 거의 안 바뀐다 — 그룹과 같은 결로 오래 둔다.
  const emoji_q = useQuery({
    queryKey: ['emoji'],
    queryFn: () => api.emoji(),
    staleTime: 5 * 60_000,
    // 파일로 단 이모지의 주소는 서명이라 최소 90분 산다(G32). 창을 오래 켜 둬도 그 안에 다시 받는다.
    refetchInterval: 30 * 60_000,
  })
  const emoji_url = new Map<string, string>()
  for (const e of emoji_q.data ?? []) emoji_url.set(e.name, e.url)
  // 본문과 반응 알약이 **같은 표**를 본다 — 두 벌이면 방금 단 이모지가 한쪽에만 뜬다.
  const emoji_of = (name: string) => emoji_url.get(name)
  // 반응 고르기 창의 맨 위 칸. 그림 주소는 위의 같은 표에서 읽는다.
  const emoji_names = emoji_q.data?.map((e) => e.name) ?? []

  // 이름표. 메시지에는 번호만 실리므로 명단은 한 번 받아 오래 쓴다 —
  // 이름이 바뀌는 일은 드물고, 바뀌면 다음 새로 고침에 따라온다.
  const members = useQuery({
    queryKey: ['members'],
    queryFn: () => api.members(),
    staleTime: 10 * 60_000,
  })
  const by_id = new Map<UserId, Member>()
  for (const p of members.data ?? []) by_id.set(p.id, p)
  const name_of = (u: UserId) => by_id.get(u)?.display_name
  const bot_of = (u: UserId) => by_id.get(u)?.is_bot === true
  // 그룹 번호 → 이름과 "내가 그 안에 있는가". 안에 있으면 나를 부른 것으로 그린다.
  const group_of = (g: GroupId) => {
    const found = (groups_q.data ?? []).find((row) => row.id === g)
    return found === undefined
      ? undefined
      : { handle: found.handle, mine: found.members.includes(as_user(me)) }
  }
  // `@이름` → 사람. 이름이 겹치면 먼저 온 사람이다 — 자동완성이 넣은 이름이라 대개 하나다.
  // 사람이 먼저다. 그룹과 이름이 겹치면 사람을 부른 것으로 읽는다 — 한 사람을
  // 부르려다 팀 전체를 깨우는 쪽이 그 반대보다 나쁘다.
  const resolve = (name: string) => {
    const person = (members.data ?? []).find((m) => m.display_name === name)?.id
    if (person !== undefined) return person
    const team = (groups_q.data ?? []).find((g) => g.handle === name)
    return team === undefined ? undefined : { group: team.id }
  }

  // 연 채널의 사람들. DM 은 이름이 없어서 여기서 상대 이름을 빌려 쓴다.
  const here_members = useQuery({
    queryKey: ['channel_members', open],
    queryFn: async () => {
      const ids = await api.channel_members(open as ChannelId)
      // 명단에 없는 사람이 있으면(방금 단 웹훅의 봇, 막 들어온 사람) 명단도 다시 받는다 — 10분을 기다리면
      // 그동안 "#13" 으로 선다(F235). 이 질의가 다시 돌 때만 보므로 되풀이되지 않는다.
      const known = queries.getQueryData<Member[]>(['members'])
      if (known && ids.some((id) => !known.some((m) => m.id === id)))
        void queries.invalidateQueries({ queryKey: ['members'] })
      return ids
    },
    enabled: open !== null,
    staleTime: 60_000,
  })

  // 초록불: 지금 보는 채널의 사람들만 묻는다. 조직 전체를 매번 묻지 않는다.
  const asked = here_members.data ?? []
  const online = useQuery({
    queryKey: ['presence', asked],
    queryFn: () => api.presence(asked),
    enabled: asked.length > 0,
    refetchInterval: PRESENCE_EVERY_MS,
    refetchIntervalInBackground: false,
  })
  const online_set = new Set(online.data ?? [])
  // 내 초록불은 서버에 묻지 않는다 — 창을 연 직후·소켓이 붙기 전엔 `last_seen_at` 이 아직 안 신선해서 창을 보고
  // 있는데도 내가 '자리 비움' 으로 떴다(3차 F63). 이 창이 서버에 붙어 있으면 나는 접속 중이다.
  const attached = useSyncExternalStore(
    session.subscribe,
    () => session.scan().conn === 'Attached',
    () => false,
  )

  // 쓰는 중: 저장소가 4초짜리 불을 켜 둔다. 나는 뺀다 — 내가 쓰는 건 내가 안다.
  // 연 채널의 명단만 **글자로** 고른다. 지도를 통째로 고르면 다른 채널에서 누가 칠 때마다,
  // 같은 사람이 3초마다 불을 다시 켤 때마다 판 전체가 다시 그려진다.
  const typing_ids = useChat((s) => typing_line(s.typing, open, me))
  const typing_names =
    typing_ids === ''
      ? []
      : typing_ids.split(',').map((who) => name_of(Number(who) as UserId) ?? `#${who}`)

  // 프로필 한 벌. 사람 수만큼 묻지 않는다 — 한 번 받아 이름 옆에 붙인다.
  const profiles = useQuery({ queryKey: ['profiles'], queryFn: () => api.profiles() })
  const profile_map = new Map((profiles.data ?? []).map((p) => [p.user, p]))
  const i_am_away = profile_map.get(as_user(me))?.away === true
  const people: Person[] | undefined = here_members.data?.map((id) => ({
    id: id,
    name: name_of(id) ?? `#${id}`,
    is_bot: by_id.get(id)?.is_bot ?? false,
    online: id === me ? attached && !i_am_away : online_set.has(id),
    // 이름 아래 한 줄(상태, 없으면 직함) — 판은 그릴 줄 아는데 넘기지 않아 늘 이름만 섰다(3차 F135).
    title: profile_map.get(id)?.title ?? null,
    status: say_status(profile_map.get(id)),
  }))

  // 타이핑 알림은 세션이 3초에 한 번으로 줄인다. 여기서는 그냥 부른다.
  const typing = () => {
    if (open !== null) session.typing(open)
  }

  // 처음 목록이 오면 하나 골라 준다. 아무것도 안 고른 화면은 비어 보여서
  // 사람들은 "안 되네" 라고 판단한다. 기기에 적어 둔 채널(F161)은 처음 한 번만 목록과 맞대 본다 —
  // 나간 채널이면 첫 채널로. 그 뒤에는 맞대지 않는다(막 만든 방은 목록보다 먼저 열린다).
  const checked = useRef(false)
  useEffect(() => {
    const list = channels.data
    const first = list?.[0]
    if (list === undefined || first === undefined) return
    const stale = !checked.current && !list.some((c) => c.id === open)
    checked.current = true
    if (open === null || stale) set_open(first.id)
  }, [channels.data, open])
  useEffect(() => {
    if (open === null) return
    keep_open(globalThis.sessionStorage, me, open)
    keep_open(globalThis.localStorage, me, open)
  }, [open, me])

  // 본문은 캐시에서. 사건이 오면 캐시가 빠진 구간만 채우고 알린다.
  const slice = useSyncExternalStore(timeline.subscribe, () => timeline.slice(open))
  useEffect(() => {
    if (open !== null) timeline.open(open).catch((e: unknown) => console.error('timeline.open', e))
  }, [timeline, open])

  // 답글은 본류에 섞여 온다(seq 가 연속이어야 캐시가 구멍 없이 채운다). 화면에서 가른다.
  const { roots, reply_counts, reply_last } = split_replies(slice.lines)

  // 명단에 없는 글쓴이(방금 만든 웹훅 봇, 새로 든 사람)가 보이면 명단을 한 번 다시 받는다 — 10분 캐시를
  // 기다리면 그동안 "#6" 이 이름 자리에 선다(3차 F85). 한 번 물은 번호는 다시 묻지 않는다(지워진 사람).
  const named = useRef(new Set<number>())
  useEffect(() => {
    if (members.data === undefined || members.isFetching) return
    const known = new Set<number>(members.data.map((m) => m.id))
    const unknown = roots.map((l) => l.author).filter((a) => !known.has(a) && !named.current.has(a))
    if (unknown.length === 0) return
    for (const a of unknown) named.current.add(a)
    void members.refetch()
  }, [roots, members.data, members.isFetching, members.refetch])

  // 보이는 줄의 반응. 모르는 것만 묶어서 한 번 센다.
  const tallies = useSyncExternalStore(reactions.subscribe, () => reactions.slice(open))
  useEffect(() => {
    if (open !== null && roots.length > 0)
      reactions.need(
        open,
        roots.map((l) => l.seq),
      )
  }, [reactions, open, roots])
  const on_react = (seq: Seq, emoji: string) => {
    if (open !== null) void reactions.toggle(open, seq, emoji).catch(() => {})
  }
  // 보이는 줄의 투표. 반응처럼 묶어서 세고, `poll_changed` 가 오면 그 줄만 다시 센다.
  const poll_map = useSyncExternalStore(polls.subscribe, () => polls.slice(open))
  useEffect(() => {
    if (open !== null && roots.length > 0)
      polls.need(
        open,
        roots.map((l) => l.seq),
      )
  }, [polls, open, roots])
  const poll_trouble = (e: unknown) =>
    toasts.show({ title: t('투표하지 못했어요'), description: why(e), tone: 'danger' })
  const on_poll_vote = (seq: Seq, choices: number[]) => {
    if (open !== null) void polls.vote(open, seq, choices).catch(poll_trouble)
  }
  const on_poll_unvote = (seq: Seq) => {
    if (open !== null) void polls.unvote(open, seq).catch(poll_trouble)
  }
  const on_poll_close = (seq: Seq) => {
    if (open !== null) void polls.close(open, seq).catch(poll_trouble)
  }

  // 첨부·링크 카드. 보이는 줄의 것만 묶어서 묻는다. 서명은 45초마다 다시 받는다.
  const file_map = useSyncExternalStore(files.subscribe, files.slice)
  const card_map = useSyncExternalStore(cards.subscribe, cards.slice)
  useEffect(() => {
    const ask = () => {
      files.need(slice.lines.flatMap((l) => attachments_of(l.body)))
      cards.need(roots.flatMap((l) => links_of(l.body)))
    }
    ask()
    // 서명이 만료되면 `need` 가 다시 묻는다 — 보이는 동안만 돌린다.
    const timer = setInterval(ask, FILE_REFRESH_MS)
    return () => clearInterval(timer)
  }, [files, cards, slice.lines, roots])

  const attach = async (file: File) => {
    if (open === null) throw new Error(t('먼저 채널을 선택해 주세요'))
    const a = await upload_file(api, open, of_file(file))
    return { id: a.id, name: a.filename }
  }

  // 채널을 열고 나가기. 새 채널은 세션에 심고(커서·배지), 목록은 다시 받는다.
  const adopt = async (c: Channel) => {
    session.adopt(c)
    await queries.invalidateQueries({ queryKey: ['channels'] })
    set_open(c.id)
  }
  // 메모는 React Compiler 가 한다 — 손으로 쓴 의존 목록은 컴파일러가 지킬 수 없었다.
  const open_kind = (channels.data ?? []).find((c) => c.id === open)?.kind
  const huddle = useHuddleHands(api, session)
  // 공지 채널에서 새 글을 쓸 수 있는가. 서버가 최종 판정하고, 여기선 입력칸을 감출 뿐이다.
  const can_announce = may_announce(members.data, me)
  // 연 채널을 내가 돌보는가 — 조직 관리자이거나 채널 관리자. 편집 판과 캔버스 지우기가 손잡이를
  // 줄지 여기서 정한다. 누구에게나 주면 구성원은 눌러 보고 나서야 "찾을 수 없어요" 를 받는다(3차 F140).
  const managers_here = useQuery({
    queryKey: ['managers', open],
    queryFn: () => api.channel_managers(open as ChannelId),
    enabled: open !== null && !can_announce && open_kind !== undefined && !is_dm(open_kind),
  })
  const manager = can_announce || (managers_here.data?.includes(me as UserId) ?? false)
  // 검색 결과의 채널 이름. 대화방은 이름이 없어 사람 이름으로 잇는다.
  const room_names = () =>
    new Map(
      (channels.data ?? []).map((c) => [
        c.id,
        c.name ?? dm_name(c, null, undefined, me, name_of, members.data !== undefined),
      ]),
    )
  // 연 채널을 떠나면 사이드바의 다음 채널로 간다(슬랙과 같다). 빈 화면에 떨어지면 사람이
  // 어디로 갈지 다시 골라야 한다. 순서는 ⌥↓ 와 같은 목록(닫은 대화방은 뺀다)이다.
  const move_on = (at: ChannelId) => {
    if (at !== open) return
    const order = (channels.data ?? [])
      .filter((c) => !hidden_after_close(summaries.get(c.id)?.last ?? c.last, c.closed_upto, false))
      .map((c) => c.id)
    set_open(step_channel(order, at, 1))
  }
  // 줄 메뉴는 연 채널이 아닌 곳에도 부른다 — 그때는 화면이 그대로다.
  const leave_at = async (at: ChannelId) => {
    await api.leave(at)
    session.drop(at)
    move_on(at)
    // 나간 공개 채널은 둘러보기의 "들어갈 곳" 이 된다 — 판이 열려 있으면 그 자리에서 다시 선다(3차 F192).
    set_browse_epoch((n) => n + 1)
    await queries.invalidateQueries({ queryKey: ['channels'] })
  }
  const close_dm_at = (at: ChannelId) =>
    told(
      toasts,
      (async () => {
        await api.close_dm(at, true)
        move_on(at)
        await queries.invalidateQueries({ queryKey: ['channels'] })
      })(),
      t('DM을 닫았어요'),
      t('DM을 닫지 못했어요'),
    )
  const manage: Manage = {
    ...(huddle === undefined ? {} : { huddle }),
    ...(ai_on
      ? {
          summarize: (channel: ChannelId, how: SummaryHow) => api.summarize(channel, how),
          answer: (question: string) => api.search_answer(question),
        }
      : {}),
    create_channel: async (kind, name) => adopt(await api.create_channel(kind, name)),
    // 다시 열면 서버가 닫기를 거둔다. `adopt` 가 목록을 다시 받아 `closed_upto` 도 사라진다.
    open_dm: async (with_) => adopt(await api.open_dm(with_)),
    invite: async (who) => {
      if (open === null) return
      if (who.length > 0) await api.invite_many(open, who)
      await queries.invalidateQueries({ queryKey: ['channel_members', open] })
    },
    ...(is_dm(open_kind) && open !== null ? { close_dm: () => close_dm_at(open) } : {}),
    leave: async () => {
      if (open !== null) await leave_at(open)
    },
    // 게스트는 들이지도 빼지도 못한다(서버 `guard_caller`) — 눌러 봐야 거절인 단추는 세우지 않는다.
    ...(open !== null &&
    open_kind !== undefined &&
    !is_dm(open_kind) &&
    !['external_guest', 'guest_role', undefined].includes(
      members.data?.find((m) => m.id === me)?.role,
    )
      ? {
          kick: {
            me: me as UserId,
            out: (who: UserId) =>
              told(
                toasts,
                (async () => {
                  await api.kick(open, who)
                  await queries.invalidateQueries({ queryKey: ['channel_members', open] })
                })(),
                t('채널에서 내보냈어요'),
                t('내보내지 못했어요. 잠시 후 다시 시도해 주세요.'),
              ),
          },
        }
      : {}),
    // 사이드바 줄 메뉴 — 연 채널이 아니어도 그 줄에 한다. 나가기만 던진다(확인 창이 까닭을 적는다).
    row: {
      pref: (channel, patch) =>
        told(
          toasts,
          (async () => {
            await api.set_pref(channel, patch)
            await queries.invalidateQueries({ queryKey: ['channel_prefs'] })
          })(),
          patch.favorite === false ? t('즐겨찾기에서 제거했어요') : t('즐겨찾기에 추가했어요'),
          t('즐겨찾기를 바꾸지 못했어요'),
        ),
      read: (channel) => {
        const upto = useChat.getState().channels.get(channel)?.last
        if (upto === undefined) return Promise.resolve()
        return told(
          toasts,
          (async () => {
            await api.mark_read(channel, upto)
            read_bump(channel, upto)
          })(),
          t('읽음으로 표시했어요'),
          t('읽음으로 표시하지 못했어요'),
        )
      },
      leave: async (channel) => {
        await leave_at(channel)
        toasts.show({ title: t('채널에서 나갔어요'), tone: 'ok' })
      },
      close_dm: close_dm_at,
    },
    edit_channel: async (patch) => {
      if (open === null) return
      const { private: shut, default_join, ...rest } = patch
      if (default_join !== undefined) {
        await api.set_default_channel(open, default_join)
        await queries.invalidateQueries({ queryKey: ['default_channels'] })
      }
      if (Object.keys(rest).length > 0 || shut !== undefined) {
        const next = await api.update_channel(
          open,
          shut === undefined ? rest : { ...rest, kind: shut ? 'private' : 'public' },
        )
        session.adopt(next)
      }
      await queries.invalidateQueries({ queryKey: ['channels'] })
      // 실패는 편집 판이 까닭을 곁에 적는다. 여기서는 판이 닫히는 성공만 알린다.
      if (rest.stored !== true) {
        toasts.show({ title: t('채널 설정을 저장했어요'), tone: 'ok' })
        return
      }
      // 보관한 채널은 사이드바에서 빠진다. 그 자리에 빈 화면을 두면 무엇이 일어났는지 모른다 —
      // 첫 채널로 옮기고(`open` 을 비우면 위의 효과가 고른다), 되돌릴 단추를 알림에 단다(F38).
      const id = open
      const name = channels.data?.find((c) => c.id === id)?.name ?? ''
      set_open(null)
      stow_toast.current = toasts.show({
        title: t('#{name} 채널을 보관했어요', { name }),
        tone: 'ok',
        action: {
          label: t('보관 취소'),
          on_click: () => {
            void api
              .update_channel(id, { stored: false })
              .then(async (back) => {
                session.adopt(back)
                await queries.invalidateQueries({ queryKey: ['channels'] })
                set_open(id)
              })
              .catch((e: unknown) => {
                toasts.show({
                  title: t('보관을 취소하지 못했어요'),
                  description: why(e),
                  tone: 'danger',
                })
              })
          },
        },
      })
    },
    remove_channel: async () => {
      if (open === null) return
      const id = open
      const name = channels.data?.find((c) => c.id === id)?.name ?? ''
      await api.remove_channel(id)
      // 채널이 없으니 첫 채널로 옮긴다(`open` 을 비우면 위의 효과가 고른다).
      session.drop(id)
      set_open(null)
      await queries.invalidateQueries({ queryKey: ['channels'] })
      toasts.show({ title: t('#{name} 채널을 삭제했어요', { name }), tone: 'ok' })
    },
    convert_dm: async (name) => {
      if (open === null) return
      session.adopt(await api.convert_dm(open, name))
      await queries.invalidateQueries({ queryKey: ['channels'] })
      toasts.show({ title: t('#{name} 비공개 채널로 바꿨어요', { name }), tone: 'ok' })
    },
    save_sections: async (list) => {
      // 접기처럼 잦은 일은 기다리지 않고 먼저 그린다. 서버 답으로 다시 맞춘다.
      queries.setQueryData(['sections'], list)
      queries.setQueryData(['sections'], await api.set_sections(list))
      await queries.invalidateQueries({ queryKey: ['channel_prefs'] })
    },
    rename_section: async (from, to) => {
      queries.setQueryData(['sections'], await api.rename_section(from, to))
      await queries.invalidateQueries({ queryKey: ['channel_prefs'] })
    },
    move_to_section: async (channel, section) => {
      await api.set_pref(channel, { section })
      await queries.invalidateQueries({ queryKey: ['channel_prefs'] })
      await queries.invalidateQueries({ queryKey: ['sections'] })
    },
    managers: (channel) => api.channel_managers(channel),
    // 바깥 조직과의 이음. 보기는 누구나, 초대·끊기는 관리자만(서버도 404).
    connect: {
      of: (channel) => api.connect_of(channel),
      ...(can_announce
        ? {
            invite: async (channel: ChannelId) => (await api.connect_invite(channel)).token,
            unlink: async (channel: ChannelId, peer: number) => {
              await api.connect_unlink(channel, peer)
              await queries.invalidateQueries({ queryKey: ['connect'] })
              await queries.invalidateQueries({ queryKey: ['channels'] })
            },
          }
        : {}),
    },
    set_manager: async (channel, who, on) => {
      await api.set_channel_manager(channel, who, on)
      await queries.invalidateQueries({ queryKey: ['managers', channel] })
    },
    manager: can_announce || managers_here.data !== undefined ? manager : undefined,
    admin: can_announce,
    set_pref: async (patch) => {
      if (open === null) return
      await api.set_pref(open, patch)
      await queries.invalidateQueries({ queryKey: ['channel_prefs'] })
      // 판이 닫히는 것만으로는 저장됐는지 몰랐다 — '멘션만' 은 사이드바에 표시도 없다(3차 F294).
      toasts.show({ title: t('알림 설정을 저장했어요'), tone: 'ok' })
    },
    activity: async () => {
      // 키워드로 울린 줄을 "멘션" 이라 부르지 않게 내 키워드를 같이 본다(3차 F171). 설정 판과 같은 캐시다.
      const [acts, mine] = await Promise.all([
        api.activity(),
        queries
          .fetchQuery({
            queryKey: ['highlights'],
            queryFn: () => api.highlights(),
            staleTime: 60_000,
          })
          .catch(() => ({ words: [] as string[] })),
      ])
      return mark_keywords(acts, me as UserId, mine.words)
    },
    my_threads: async () => to_briefs(await api.my_threads()),
    unreads: async () => {
      const wanted = unread_targets(picks, (id) => useChat.getState().channels.get(id)?.read_upto)
      const got = await Promise.all(
        wanted.map(async (w) => {
          const lines = await api.latest(w.channel, Math.min(w.count, UNREAD_TAKE))
          return unread_group(w, lines)
        }),
      )
      return got.filter((g) => g.lines.length > 0)
    },
    read_upto: async (channel, upto) => {
      await api.mark_read(channel, upto)
      read_bump(channel, upto)
    },
    forward: (from, seq, to) => {
      // 전달할 줄은 이미 눈앞에 있다 — 보고 있던 것만 전달할 수 있으니 다시 묻지 않는다.
      const line = slice.lines.find((l) => l.seq === seq)
      if (line === undefined) return Promise.resolve()
      const target = (channels.data ?? []).find((c) => c.id === to)
      const source = (channels.data ?? []).find((c) => c.id === from)
      const who = name_of(line.author) ?? t('알 수 없는 사용자')
      // 어디서 온 글인지와 원문으로 가는 길을 머리에 싣는다(3차 F160). 대화방은 이름을 싣지 않는다 —
      // 누구와 나눈 대화인지까지 옮겨 적을 까닭이 없다. 링크는 들어간 사람에게만 열린다.
      const head =
        source === undefined || is_dm(source.kind)
          ? t('{name} 님의 메시지', { name: who })
          : t('{name} 님의 메시지 · #{channel}', { name: who, channel: source.name ?? '' })
      const where = is_dm(target?.kind)
        ? (room_names().get(to) ?? '')
        : `#${target?.name ?? room_names().get(to) ?? ''}`
      return told(
        toasts,
        api
          .send(
            to,
            quoted(line, head, { url: link_to(from, seq), view: t('원문 보기') }),
            fresh_msg_id(),
          )
          .then(async (sent) => {
            // 내가 보낸 글이 그 채널을 굵게 만들면 안 된다(3차 F229). 앞에 안 읽은 글이 있었으면 그대로 둔다 —
            // 전달 한 번에 남의 글까지 읽은 것이 되면 안 된다.
            const was = useChat.getState().channels.get(to)?.read_upto
            if (was === undefined || was < sent.seq - 1) return
            await api.mark_read(to, sent.seq)
            read_bump(to, sent.seq)
          }),
        t('{channel}에 전달했어요', { channel: where }),
        t('메시지를 전달하지 못했어요'),
      )
    },
    read_all: () => {
      const work = async () => {
        await api.read_all()
        await queries.invalidateQueries({ queryKey: ['read_state'] })
        read_all()
      }
      return told(
        toasts,
        work(),
        t('모두 읽음으로 표시했어요'),
        t('모두 읽음으로 표시하지 못했어요'),
      )
    },
    lists: {
      all: (ch) => api.lists(ch as ChannelId),
      one: (ch, id) => api.list_one(ch as ChannelId, id),
      create: async (ch, title, columns) => {
        await api.list_create(ch as ChannelId, title, [...columns])
      },
      remove: (ch, id) => api.list_remove(ch as ChannelId, id),
      edit: async (ch, id, title, columns) => {
        await api.list_edit(ch as ChannelId, id, title, [...columns])
      },
      add_row: async (ch, id, cells) => {
        await api.list_add_row(ch as ChannelId, id, cells)
      },
      edit_row: async (ch, id, row, version, cells) => {
        await api.list_edit_row(ch as ChannelId, id, row, version, cells)
      },
      remove_row: (ch, id, row) => api.list_remove_row(ch as ChannelId, id, row),
    },
    // 캔버스는 연 동안 소켓 사건을 스스로 엿듣는다. 판이 닫히면 `close` 가 구독을 거둔다.
    canvas: {
      open: (ch, listen) =>
        open_canvas({
          api,
          on: (tap) => session.on(tap),
          channel: ch as ChannelId,
          listen,
          me,
          say: (at, end, left) => session.canvas_here(ch as ChannelId, at, end, left),
        }),
    },
    bookmarks: (channel) => api.bookmarks(channel),
    bookmark_add: async (channel, title, url) => {
      await api.bookmark_add(channel, title, url)
    },
    bookmark_remove: (channel, id) => api.bookmark_remove(channel, id),
    bookmark_edit: async (channel, id, title, url) => {
      await api.bookmark_edit(channel, id, title, url)
    },
    pins: async (channel) => to_marks(await api.pins(channel)),
    saved_marks: async () => to_marks(await api.saved_items()),
    on_later: async (channel, seq, patch) => {
      await api.later_item(channel, seq, patch)
      await queries.invalidateQueries({ queryKey: ['saved'] })
    },
    bookings_rev: booked,
    bookmarks_rev: marks_rev,
    bookings: async () =>
      (await api.scheduled()).map((b) => ({
        id: b.id,
        channel: b.channel,
        line: peek_of(b.blocks),
        due_at: b.due_at,
        failed_why: b.failed_why,
        blocks: b.blocks,
      })),
    booking_names: { user: name_of, group: (g) => group_of(g)?.handle },
    unbook: async (id) => {
      await api.unschedule(id)
      set_booked((n) => n + 1)
    },
    // 지금 보내면 그 채널에 줄이 선다 — 소켓이 곧 알리지만, 판은 곧장 다시 받는다.
    book_now: async (id) => {
      await api.send_scheduled(id)
      set_booked((n) => n + 1)
    },
    rebook: async (id, change) => {
      await api.reschedule(id, change, fresh_msg_id())
      set_booked((n) => n + 1)
    },
    on_pin: (channel, seq, on) => mark('pin', channel, seq, on),
    on_save: (channel, seq, on) => mark('save', channel, seq, on),
    browse: async () => {
      const found = await api.browse_channels()
      return found.map((c) => ({ ...c, name: c.name ?? t('대화') }))
    },
    browse_epoch,
    archived: async () => {
      const found = await api.archived_channels()
      return found.map((c) => ({ ...c, name: c.name ?? t('대화') }))
    },
    unarchive: async (id) => {
      session.adopt(await api.update_channel(id, { stored: false }))
      set_browse_epoch((n) => n + 1)
      await queries.invalidateQueries({ queryKey: ['channels'] })
      // 보관할 때 띄운 "되돌리기" 알림은 저절로 닫히지 않는다 — 여기서 되살렸는데 그 단추가 남아 있으면
      // 누를 때 "바뀐 것이 없다" 로 실패한다. 닫고, 되살린 채널로 데려간다 — 채널이 열리는 것이 곧 소식이다(3차 F187).
      if (stow_toast.current !== null) toasts.dismiss(stow_toast.current)
      stow_toast.current = null
      set_open(id)
    },
    join: async (id) => {
      await join_or_explain(
        () => api.join(id),
        () => set_browse_epoch((n) => n + 1),
      )
      // 들어간 채널은 "들어갈 곳" 이 아니다. 목록을 다시 받지 않으면 [참여] 가 그대로 서 있어, 들어갔는지
      // 되묻게 된다(3차 F192).
      set_browse_epoch((n) => n + 1)
      adopt(await api.channel(id))
    },
    // 들기 전에 훑어본다(G35). 공개 채널은 들지 않아도 읽힌다(`must_see`). 지운 줄·답글은 판이 뺀다.
    peek: (id) => api.latest(id, 40),
    search: async (text, before) => {
      const ask = await parse_query(text, search_names(members.data, channels.data))
      const hits = await api.search(before === undefined ? ask : { ...ask, before })
      const names = room_names()
      return hits.map(
        (h): Hit => ({
          channel: h.channel,
          channel_name: names.get(h.channel) ?? String(h.channel),
          line: h.message,
        }),
      )
    },
    // 수정자는 같은 풀이를 쓰되 채널·사람·기간만 싣는다. `has:pin` 따위는 글의 성질이다.
    search_files: async (text, before) => {
      const {
        text: words,
        channel,
        author,
        after,
        until,
      } = await parse_query(text, search_names(members.data, channels.data))
      const got = await api.search_files({
        text: words,
        ...(channel === undefined ? {} : { channel }),
        ...(author === undefined ? {} : { author }),
        ...(after === undefined ? {} : { after }),
        ...(until === undefined ? {} : { until }),
        ...(before === undefined ? {} : { before }),
      })
      const names = room_names()
      return got.map(
        ({ attachment: a, seq }): FileHit => ({
          id: a.id,
          channel: a.channel,
          channel_name: names.get(a.channel) ?? String(a.channel),
          seq,
          filename: a.filename,
          uploader: a.uploader,
          bytes: a.bytes,
          created_at: a.created_at,
        }),
      )
    },
  }
  const directory: Person[] = (members.data ?? [])
    .filter((m) => m.id !== me && !m.disabled)
    .map((m) => {
      const p = profile_map.get(m.id)
      return {
        id: m.id,
        name: m.display_name,
        is_bot: m.is_bot,
        online: false,
        title: p?.title ?? null,
        status: say_status(p),
      }
    })

  // 나에 관한 사건: 초대받으면 목록에 나타나고, 호출받으면 창이 안 보일 때 알린다.
  // 알림의 이름은 그때의 명단으로 읽는다 — 명단이 바뀌었다고 구독을 다시 걸 일은 아니다.
  const name_now = useEffectEvent((u: UserId) => name_of(u))
  useEffect(
    () =>
      session.on((e: Event) => {
        if (e.t === 'membership' && e.who === me) on_my_membership(e, session, api, queries)
        for_me_only(e, me, set_whispers, (id) => void show_view(api, id, set_modals))
        if (hides_from_browse(e)) set_browse_epoch((n) => n + 1)
        // 바뀜 사건(종류·이름·핀·이모지·그룹·사람)은 값을 싣지 않는다 — 그 조회를 다시 받는다.
        for (const queryKey of stale_queries(e)) void queries.invalidateQueries({ queryKey })
        if (e.t === 'bookmarks_changed') set_marks_rev((n) => n + 1)
        if (e.t === 'mentioned' || e.t === 'thread_replied' || e.t === 'reaction_received') {
          void queries.invalidateQueries({ queryKey: ['activity-peek'] })
        }
        // 채널 명단에 없는 사람이 새 줄을 썼다 — 방금 든 사람이나 방금 단 웹훅의 봇이다. 들어옴 사건은 당사자에게만
        // 가므로, 이미 받아 둔 명단이 1분 동안 옛 수로 서지 않게 다시 받는다(3차 F245).
        if (e.t === 'message') {
          const ids = queries.getQueryData<readonly UserId[]>(['channel_members', e.channel])
          if (ids !== undefined && !ids.includes(e.author))
            void queries.invalidateQueries({ queryKey: ['channel_members', e.channel] })
        }
        // 내 이름으로 새 줄이 섰다 — 예약이 나갔을 수 있다. 예약 판이 보낸 것을 붙들고 있지 않게.
        if (e.t === 'message' && e.author === me) {
          set_booked((n) => n + 1)
        }
        if (platform === undefined || platform.visible()) return
        const rooms = channels.data ?? []
        const level_of = (c: ChannelId) => level_in(prefs.data, c)
        const is_dm = (c: ChannelId) => rooms.some((r) => r.id === c && is_dm_kind(r.kind))
        const is_note = (c: ChannelId) =>
          rooms.some((r) => r.id === c && r.people?.length === 1 && r.people[0] === me)
        if (!wants_alert(e, notify.scope, level_of, is_dm, as_user(me), is_note)) return
        // 멘션은 `message` 와 `mentioned` 두 사건으로 온다 — 한 줄에 알림은 하나.
        const key = `${e.t === 'reaction_received' ? 'r' : ''}${e.channel}:${e.seq}`
        if (announced.has(key)) return
        announced.add(key)
        if (announced.size > 200) announced.delete(announced.values().next().value as string)
        if (notify.sound) ring()
        void announce(platform, api, e.channel, e.seq, name_now, rooms)
      }),
    [session, me, api, queries, platform, channels.data, prefs.data, notify, announced],
  )

  // 스레드. 루트는 본류 캐시에서, 답글은 스레드 요청으로. 캐시에 답글이 새로 들어오면
  // (사건 → 구간 채움) 답글 수가 바뀌고, 그것이 키에 들어 있어 다시 받는다.
  // 채널과 함께 적어 둔다 — 채널을 바꾸면 저절로 닫힌다. 효과로 지우면 한 프레임 늦다.
  const [thread_at, set_thread_at] = useState<{ channel: ChannelId; root: Seq } | null>(null)
  // 채널을 떠나면 그 스레드는 버린다 — 적어 둔 채로 두면 한참 뒤 그 채널로 돌아올 때(대화방 닫기·검색 뛰기)
  // 잊은 스레드가 옆 자리를 불쑥 차지했다(3차 F311). 옮기면서 여는 스레드(모아 보기)는 새 채널의 것이라 남는다.
  const [thread_for, set_thread_for] = useState(open)
  if (thread_for !== open) {
    set_thread_for(open)
    if (thread_at !== null && thread_at.channel !== open) set_thread_at(null)
  }
  const thread_root = thread_root_of(thread_at, open)
  // 채널을 함께 주면 그 채널의 스레드다 — 스레드 모아 보기는 채널을 옮기면서 연다(3차 F185).
  // 답글 번호가 오면 그 뿌리를 연다 — "채널에도 보낸" 답글은 본류에 서서 손 막대의 [스레드에서 답글 달기]가
  // 제 번호를 준다. 그대로 열면 서버가 스레드가 아니라며 404 를 주고(뿌리만 스레드다) 답글 보내기도 404 로
  // 실패했다. `thread_of` 는 시험만 있고 아무 데서도 부르지 않던 자리다.
  const set_thread_root = (root: Seq | null, at?: ChannelId | null) => {
    const channel = at === undefined ? open : at
    if (root === null || channel === null) {
      set_thread_at(null)
      return
    }
    set_thread_at({ channel, root: thread_of(timeline.slice(channel).lines, root) })
  }
  const root_line = roots.find((l) => l.seq === thread_root)
  // 판을 열 때 한 번 받는다. 그 뒤의 답글은 본류 사건으로 캐시에 들어오므로 거기서 잇는다 —
  // 답글 수를 열쇠에 넣으면 답글이 하나 올 때마다 스레드 전체를 다시 받는다.
  const thread_q = useQuery({
    queryKey: ['thread', open, thread_root],
    queryFn: () => api.thread(open as ChannelId, thread_root as Seq),
    enabled: open !== null && thread_root !== null,
    staleTime: 60_000,
  })
  // 따르는가. 켜고 끄면 곧바로 다시 묻는다 — 서버가 참여 여부까지 보고 답한다.
  // 내 키워드 — 대화 속에서 칠하고(3차 F169) 활동 줄을 가른다(F171). 설정 판과 같은 캐시다.
  const highlights_q = useQuery({
    queryKey: ['highlights'],
    queryFn: () => api.highlights(),
    staleTime: 60_000,
  })
  const follow_q = useQuery({
    queryKey: ['thread-follow', open, thread_root],
    queryFn: () => api.thread_following(open as ChannelId, thread_root as Seq),
    enabled: open !== null && thread_root !== null,
    staleTime: 60_000,
  })
  const thread = ((): ThreadView | undefined => {
    if (thread_root === null || root_line === undefined || open === null) return undefined
    const replies = thread_replies(thread_q.data, slice.lines, thread_root)
    return {
      root: root_line,
      replies: replies,
      loading: thread_q.isPending,
      close: () => set_thread_root(null),
      draft: () => book.of(thread_draft(open, thread_root)),
      on_draft: (text) => {
        sync.edit(thread_draft(open, thread_root), text)
      },
      following: follow_q.data,
      ...(ai_on ? { summarize: () => api.summarize(open, { thread: thread_root }) } : {}),
      set_following: (on) => {
        api
          .follow_thread(open, thread_root, on)
          .then(() => queries.invalidateQueries({ queryKey: ['thread-follow', open, thread_root] }))
          .catch(() => {})
      },
      reply: async (written, also_channel, attached) => {
        const blocks = with_files(body_of(written, resolve), attached)
        if (blocks.length === 0) return
        const sent = await api.send(open, blocks, fresh_msg_id(), thread_root, also_channel)
        timeline.sent(open, sent.seq)
      },
    }
  })()

  // 스레드 답글의 반응도 센다 — 본류는 뿌리만 센다.
  useEffect(() => {
    if (open !== null && thread !== undefined && thread.replies.length > 0)
      reactions.need(
        open,
        thread.replies.map((l) => l.seq),
      )
  }, [reactions, open, thread])

  const here = summaries.get(open ?? (-1 as ChannelId))
  const last = here?.last ?? (0 as Seq)

  // 연 순간의 읽음 위치. 채널이 바뀔 때만 다시 잡는다.
  // 렌더 중에 ref 를 읽고 쓰면 React Compiler 가 Room 전체를 건너뛴다. "props 에서 끌어낸
  // 상태" 모양으로 쓴다 — 렌더 중 set 은 React 가 커밋 전에 곧바로 다시 그린다.
  // `caught_up` — 열 때 다 읽었었다. 그러면 보는 동안 온 글은 읽는 중에 온 글이라 띠를 세우지 않는다.
  // 얼린 값을 그대로 쓰면 내 글 바로 밑, 방금 온 봇의 답 위에 "새 메시지 1개" 가 섰다(3차 F145).
  const [frozen, set_frozen] = useState<{
    channel: ChannelId | null
    upto: Seq
    caught_up: boolean
  }>({ channel: null, upto: 0 as Seq, caught_up: false })
  // 요약(읽음 위치)이 오기 전에는 얼리지 않는다. 새로 고친 직후엔 채널이 먼저 열리고 요약이 뒤에 와서,
  // 0 으로 얼리면 다 읽은 채널에 "새 메시지" 띠가 섰다(3차 F129). 모르는 동안은 띠가 없다.
  if (frozen.channel !== open && here)
    set_frozen({ channel: open, upto: here.read_upto, caught_up: here.read_upto >= here.last })

  useEffect(() => {
    if (open === null || last <= 0) return
    if ((here?.read_upto ?? 0) >= last) return
    // 실패해도 조용히 넘긴다. 다음에 열 때 다시 올린다 — 읽음 표시 때문에
    // 대화 화면에 오류를 띄우는 건 과하다.
    api
      .mark_read(open, last)
      .then(() => read_bump(open, last))
      .catch(() => {})
  }, [api, open, last, here?.read_upto, read_bump])

  // 검색에서 뛰기. 채널을 먼저 열어야 타임라인이 그 채널을 쥔다.
  // 가리킨 줄이 답글이면 본류에 그 줄이 없다 — 스레드를 열고 뿌리로 뛴다. 활동의 답글·스레드 속 멘션을 누르면
  // 채널만 열리고 아무 일도 없던 자리다(3차 F340).
  const jump = async (channel: ChannelId, seq: Seq) => {
    set_open(channel)
    const ok: boolean = await timeline.reach(channel, seq).catch(() => false)
    const root = ok && timeline.slice(channel).lines.find((l) => l.seq === seq)?.parent
    if (!root) return ok
    set_thread_root(root, channel)
    return jump(channel, root)
  }
  const [aim, set_aim] = useState<{ channel: ChannelId; seq: Seq } | null>(null)
  const [link, set_link] = useState<{ channel: ChannelId; seq: Seq } | null>(null)
  // 링크가 가리킨 곳이 내 목록에 있어야 연다. 없으면(안 든 채널·남의 대화방·지워진 방) 보던 채널을 그대로 두고
  // 까닭을 말한다 — 빈 "채널을 골라 주세요" 로 떨어지던 자리다(3차 F166). 새로 고침처럼 목록보다 먼저 온 링크는
  // 목록을 기다린다. 열 수 있으면 화면(Workspace)에 맡긴다 — 검색처럼 그 줄로 옮기고 강조까지 한다(F165).
  useEffect(() => {
    const list = channels.data
    if (link === null || list === undefined) return
    set_link(null)
    if (list.some((c) => c.id === link.channel)) {
      set_aim(link)
      return
    }
    toasts.show({
      title: t('이 메시지를 열 수 없어요'),
      description: t('들어가 있지 않은 채널이거나 지워진 대화예요.'),
      tone: 'warn',
    })
  }, [link, channels.data, toasts, t])
  // 해시로 들어온 좌표를 따라간다. 처음 한 번과, 링크를 다시 누를 때마다.
  // 효과 사건(useEffectEvent)으로 둔다 — `jump` 가 새로 만들어져도 구독을 다시 걸거나
  // 지난 해시를 다시 따라가지 않는다.
  const follow = useEffectEvent(() => {
    const at = read_link(globalThis.location?.hash ?? '')
    if (at === null) return
    set_link({ channel: at.channel, seq: at.seq })
    // 따라갔으면 주소창에서 뗀다. 남겨 두면 다른 채널로 옮긴 뒤 새로 고칠 때 옛 줄로 끌려간다(3차 F161).
    // 같은 링크를 다시 누르면 해시가 다시 붙으므로 `hashchange` 가 난다.
    put_hash('')
  })
  useEffect(() => {
    const on_hash = () => follow()
    on_hash()
    globalThis.addEventListener?.('hashchange', on_hash)
    return () => globalThis.removeEventListener?.('hashchange', on_hash)
  }, [])

  const profile_of = async (who: UserId) => {
    const m = await api.member_one(who)
    const p = profile_map.get(who)
    return {
      title: p?.title ?? null,
      timezone: p?.timezone ?? null,
      status: say_status(p),
      id: as_user(m.id),
      display_name: m.display_name,
      email: m.email,
      role: m.role,
      disabled: m.disabled,
      is_bot: m.is_bot,
    }
  }

  const on_reach_top = () => {
    if (open !== null) void timeline.older(open).catch(() => {})
  }

  const pref_of = new Map((prefs.data ?? []).map((p) => [p.channel, p]))
  // 대화방의 나 아닌 사람들 — 얼굴을 고른다(3차 F255). 목록이 `people` 을 싣고, 옛 서버면 연 방만 그 방 명단으로.
  const others_in = (c: Channel) =>
    (c.people ?? (c.id === open ? here_members.data : undefined))?.filter((p) => p !== me)
  // 기본 채널 표시는 관리자에게만 쓸모가 있다 — 아니면 묻지도 않는다.
  const defaults = useQuery({
    queryKey: ['default_channels'],
    queryFn: async () => new Set(await api.default_channels()),
    enabled: can_announce,
  })
  // 바깥과 이은 채널. 머리글에 "누구와 공유" 를 건다. 잇기는 드문 일이라 오래 들고 있는다.
  const shared = useQuery({
    queryKey: ['connect'],
    queryFn: () => api.connect_mine(),
    staleTime: 5 * 60_000,
  })
  const picks = channel_picks({
    channels: channels.data ?? [],
    external: external_of(shared.data ?? []),
    summaries,
    pref_of,
    open,
    can_announce,
    defaults: defaults.data,
    name_of_dm: (c) => dm_name(c, open, here_members.data, me, name_of, members.data !== undefined),
    others_in,
    self: { user: me as UserId, name: name_of(me as UserId) ?? '' },
  })

  // 탭 제목에 지금 자리를 싣는다(3차 F219). 대화방은 사이드바 줄과 같은 사람 이름으로.
  const at_row = picks.find((p) => p.id === open)
  const place = [at_row && (at_row.dm ? at_row.name : `#${at_row.name}`), org?.name]
    .filter(Boolean)
    .join(' - ')
  useEffect(() => {
    platform?.place?.(place || null)
    return () => platform?.place?.(null)
  }, [platform, place])

  // 시작하기(F32) — 새 워크스페이스를 만든 관리자에게만, 다 했거나 닫기 전까지. 체크는 자료가 한다.
  const [starter_off, set_starter_off] = useState(
    () => read_seen(globalThis.localStorage, starter_key(me)) !== null,
  )
  const my_role = (members.data ?? []).find((m) => m.id === me)?.role
  const mine = profile_map.get(as_user(me))
  const humans = (members.data ?? []).filter((m) => !m.is_bot && !m.disabled).length
  // 초대를 보냈으면 초대한 것이다 — 받은 사람이 들어올 때까지 체크가 안 켜지면 한 일을 안 한 일로 센다(3차 F248).
  // 혼자 있는 관리자에게 시작하기가 서 있을 때만 묻는다.
  const ask_sent =
    !starter_off &&
    members.data !== undefined &&
    humans <= 1 &&
    (my_role === 'owner' || my_role === 'admin')
  // 설정 판을 다녀오면 방이 새로 서므로 그때 다시 묻는다. 두 목록을 한 번에 물어 첫 화면 코드를 줄인다.
  const invited = useQuery({
    queryKey: ['invited-any'],
    queryFn: async () => (await api.invites()).length + (await api.invite_links()).length > 0,
    enabled: ask_sent,
  })
  const steps = [
    {
      key: 'invite' as const,
      label: msg('팀원 초대하기'),
      done: humans > 1 || invited.data === true,
      ...(settings === undefined ? {} : { go: () => settings('members') }),
    },
    {
      key: 'channel' as const,
      label: msg('채널 만들기'),
      // 가입이 채널을 깔아 주지 않는다 — 첫 채널이 곧 만든 채널이다. "둘 이상" 이면 하나 만들어도 안 켜진다(3차 F223).
      done: picks.some((c) => c.dm !== true),
    },
    {
      key: 'profile' as const,
      label: msg('프로필 채우기'),
      done: (mine?.title ?? '') !== '' || (mine?.status_text ?? '') !== '',
      ...(settings === undefined ? {} : { go: () => settings('profile') }),
    },
  ]
  const starter =
    starter_off ||
    members.data === undefined ||
    (my_role !== 'owner' && my_role !== 'admin') ||
    steps.every((s) => s.done)
      ? undefined
      : {
          steps,
          dismiss: () => {
            write_seen(globalThis.localStorage, starter_key(me), new Date().toISOString())
            set_starter_off(true)
          },
        }

  const all_names = room_names()
  return (
    <>
      <Workspace
        aim={aim}
        channels={picks}
        channels_loading={channels.data === undefined}
        room_name={(id) => all_names.get(id)}
        sections={sections.data ?? []}
        sidebar_view={sidebar_view}
        set_sidebar_view={set_sidebar_view}
        all_dms={(channels.data ?? [])
          .filter((c) => c.kind === 'dm' || c.kind === 'group_dm')
          .map((c) => {
            const sum = summaries.get(c.id)
            return {
              id: c.id,
              name: all_names.get(c.id) ?? t('대화'),
              unread: sum === undefined ? 0 : Math.max(0, sum.last - sum.read_upto),
              mention_count: sum?.mention_count ?? 0,
              closed: hidden_after_close(sum?.last ?? c.last, c.closed_upto, false),
            }
          })}
        open={open}
        pick={set_open}
        lines={roots}
        tallies={tallies}
        polls={poll_map}
        on_poll_vote={on_poll_vote}
        on_poll_unvote={on_poll_unvote}
        on_poll_close={on_poll_close}
        reply_counts={reply_counts}
        reply_last={reply_last}
        on_react={on_react}
        on_thread={set_thread_root}
        {...only('thread', thread)}
        read_upto={frozen.channel === open && !frozen.caught_up ? frozen.upto : (Infinity as Seq)}
        loading={slice.loading}
        reached_start={slice.reached_start}
        on_reach_top={on_reach_top}
        jump={jump}
        profile_of={profile_of}
        name_of={name_of}
        bot_of={bot_of}
        group_of={group_of}
        emoji_of={emoji_of}
        emoji_names={emoji_names}
        groups={groups}
        me={as_user(me)}
        {...only('self', self_of(as_user(me), name_of(as_user(me)), profile_map.get(as_user(me))))}
        {...only('sign_out', sign_out)}
        snooze={async (until) => {
          await api.set_profile({ snooze_until: until })
          await queries.invalidateQueries({ queryKey: ['profiles'] })
        }}
        set_status={async (status) => {
          await api.set_profile({ status })
          await queries.invalidateQueries({ queryKey: ['profiles'] })
        }}
        set_away={async (on) => {
          await api.set_profile({ away: on })
          await queries.invalidateQueries({ queryKey: ['profiles'] })
          await queries.invalidateQueries({ queryKey: ['presence'] })
        }}
        typing={typing}
        typing_names={typing_names}
        whispers={whispers.filter((w) => w.channel === open)}
        dismiss_whisper={(key) => set_whispers((w) => w.filter((x) => x.key !== key))}
        {...only('people', people)}
        attach={attach}
        {...only('record', can_record() ? start_clip : undefined)}
        {...only('transcribe', transcribe)}
        files={file_map}
        cards={card_map}
        manage={manage}
        activity_news={activity_news}
        {...only('org', org)}
        {...only('starter', starter)}
        pinned={pinned_seqs}
        words={highlights_q.data?.words}
        saved={saved_seqs}
        drafts={drafts}
        draft_of={(c) => book.of(c)}
        on_draft={on_draft}
        on_pin={(seq, on) => {
          if (open !== null) void mark('pin', open, seq, on)
        }}
        on_save={(seq, on) => {
          if (open !== null) void mark('save', open, seq, on)
        }}
        on_link={(seq) => {
          if (open === null) return
          void copy_text(link_to(open, seq)).then((done) =>
            toasts.show(
              done
                ? { title: t('링크를 복사했어요'), tone: 'ok' }
                : {
                    title: t('링크를 복사하지 못했어요'),
                    description: t('브라우저가 클립보드 쓰기를 막았어요.'),
                    tone: 'danger',
                  },
            ),
          )
        }}
        globals={shortcut_menu.globals}
        on_global={async (channel, key) => {
          const at = from_key(key)
          if (at !== null) await api.shortcut_run(at.app, at.callback_id, channel)
        }}
        shortcuts={shortcut_menu.messages}
        on_shortcut={async (seq, key) => {
          const at = from_key(key)
          if (open !== null && at !== null)
            await api.shortcut_run(at.app, at.callback_id, open, seq)
        }}
        on_action={async (seq, block, action, value) => {
          if (open === null) return
          // 앱의 답은 사건으로 온다(글 고치기·속삭임). 여기서는 알리기만 한다.
          await api.message_action(open, seq, block, action, value)
        }}
        on_remind={(seq, at) => {
          if (open === null) return
          // 기한은 담아 둔 줄에만 걸린다 — 안 담았으면 먼저 담는다(다시 담아도 탭은 그대로다).
          const here = open
          void told(
            toasts,
            (async () => {
              if (!saved_seqs.has(seq)) await api.save_item(here, seq, true)
              await api.later_item(here, seq, { remind_at: at })
              await queries.invalidateQueries({ queryKey: ['saved'] })
            })(),
            // 언제인지 말한다 — "설정했어요" 만으로는 [직접 지정…] 에서 고른 때가 맞게 잡혔는지 모른다(3차 F275).
            t('{when}에 알려 드릴게요. 나중에 보기에서 확인할 수 있어요.', {
              when: fmt_when_in(at),
            }),
            t('리마인더를 설정하지 못했어요'),
          )
        }}
        on_unread={(seq) => {
          if (open === null) return
          // 되돌린 뒤에는 그 채널을 떠난다. 남아 있으면 다음 스크롤이 곧장 다시 읽는다.
          const before = (seq - 1) as Seq
          void told(
            toasts,
            api.mark_unread(open, before === 0 ? (0 as Seq) : before).then(() => {
              read_set(open, before)
              move_on(open)
            }),
            t('읽지 않음으로 표시했어요'),
            t('읽지 않음으로 표시하지 못했어요'),
          )
        }}
        directory={directory}
        {...(commands_q.data === undefined ? {} : { commands })}
        run={run}
        load_commands={load_commands}
        {...only('settings', settings)}
        send={async (written, attached) => {
          if (open === null) return
          const blocks = with_files(body_of(written, resolve), attached)
          if (blocks.length === 0) return
          const sent = await api.send(open, blocks, fresh_msg_id())
          // 소켓이 곧 같은 줄을 알리지만, 자기가 쓴 말은 **즉시** 보여야 한다.
          // 캐시가 그 줄까지의 구간만 받는다 — 전체를 다시 긁지 않는다.
          timeline.sent(open, sent.seq)
        }}
        poll={async (draft) => {
          if (open === null) return
          // 질문을 글로 먼저 보내고 그 글에 투표를 단다. 달기가 실패하면 글만 남는다 — 지우면 된다.
          const sent = await api.send(open, body_of(draft.question, resolve), fresh_msg_id())
          timeline.sent(open, sent.seq)
          polls.put(await api.poll_create(open, sent.seq, draft))
        }}
        schedule={async (written, attached, due_at) => {
          if (open === null) return
          const blocks = with_files(body_of(written, resolve), attached)
          if (blocks.length === 0) return
          await api.schedule(open, blocks, fresh_msg_id(), due_at)
          set_booked((n) => n + 1)
        }}
        on_edit={async (seq: Seq, blocks: Block[]) => {
          if (open === null) return
          await api.edit(open, seq, blocks)
          timeline.on_event({ t: 'edited', channel: open, seq })
        }}
        on_delete={async (seq: Seq) => {
          if (open === null) return
          await api.delete(open, seq)
          timeline.on_event({ t: 'deleted', channel: open, seq })
        }}
        on_hide_card={(seq: Seq, url: string) => {
          if (open === null) return
          const at = open
          // 실패는 조용히 둔다 — 카드는 그대로 남아 있고 다시 누르면 된다.
          void api.hide_card(at, seq, url).then(
            () => timeline.on_event({ t: 'edited', channel: at, seq }),
            () => {},
          )
        }}
      />
      {modals.length > 0 && (
        <Suspense fallback={null}>
          {modals.map((modal, i) => (
            // 아래 창은 닫지 않고 숨길 뿐이다 — 위 창을 닫으면 적던 글이 그대로 보여야 한다.
            // 앱이 창을 바꾸면(`turn`) 칸을 처음 값으로 새로 채운다. 칸별 오류에는 친 글을 지킨다.
            <ViewModal
              key={`${modal.id}.${modal.turn}`}
              open={i === modals.length - 1}
              view={modal.view}
              errors={modal.errors}
              busy={modal.busy}
              failure={modal.failure}
              name_of={name_of}
              me={as_user(me)}
              group_of={group_of}
              emoji_of={emoji_of}
              on_submit={(values) => void submit_view(api, modal, values, set_modals)}
              on_close={() => {
                // 닫기는 기다리지 않는다. 서버가 못 받아도 다음 창이 이 줄을 밀어낸다.
                api.view_close(modal.id).catch(() => {})
                set_modals((s) => popped(s, modal.id))
              }}
            />
          ))}
        </Suspense>
      )}
    </>
  )
}

/** 나에게만 오는 것 — 앱의 속삭임과 앱이 연 창. */
function for_me_only(
  e: Event,
  me: number,
  set_whispers: (f: (w: readonly Whisper[]) => readonly Whisper[]) => void,
  open_view: (id: number) => void,
) {
  if (e.t === 'ephemeral' && e.who === me) set_whispers((w) => heard(w, e))
  if (e.t === 'view_opened' && e.who === me) open_view(e.view)
}

async function show_view(api: MaruApi, id: number, set: (f: (s: Stack) => Stack) => void) {
  try {
    const got = await api.view_get(id)
    set((s) => placed(s, got))
  } catch {
    // 이미 닫혔거나 새 창에 밀렸다. 사람이 다시 누르면 새 창이 온다.
  }
}

async function submit_view(
  api: MaruApi,
  m: Modal,
  values: Record<string, string>,
  set: (f: (s: Stack) => Stack) => void,
) {
  set((s) => patched(s, m.id, (x) => ({ ...x, busy: true, failure: undefined })))
  try {
    const got = await api.view_submit(m.id, values)
    if (got.outcome === 'closed') set((s) => popped(s, m.id))
    else if (got.outcome === 'cleared') set(() => [])
    else if (got.outcome === 'errors')
      set((s) => patched(s, m.id, (x) => ({ ...x, busy: false, errors: got.errors })))
    else if (got.outcome === 'updated')
      set((s) => placed(s, { id: m.id, view: got.view, under: null }))
    else
      set((s) =>
        placed(
          patched(s, m.id, (x) => ({ ...x, busy: false })),
          { id: got.id, view: got.view, under: m.id },
        ),
      )
  } catch (err) {
    const why =
      err instanceof ApiError && err.status === 404
        ? translate(locale_now(), msg('창이 이미 닫혔어요.'))
        : trouble(err)
    set((s) => patched(s, m.id, (x) => ({ ...x, busy: false, failure: why })))
  }
}

type Summaries = ReturnType<typeof chat_store.getState>['channels']
type Pref = ChannelPref

/** 사이드바 한 줄씩. 닫아 둔 대화방은 새 글이 없으면 뺀다. 연 채널은 닫혔어도 남긴다. */
/** 채널마다 이어진 조직 이름. */
function external_of(links: readonly ConnectLink[]): ReadonlyMap<ChannelId, string[]> {
  const out = new Map<ChannelId, string[]>()
  for (const l of links) out.set(l.channel, [...(out.get(l.channel) ?? []), l.peer_name])
  return out
}

function channel_picks(a: {
  channels: readonly Channel[]
  external: ReadonlyMap<ChannelId, string[]>
  summaries: Summaries
  pref_of: ReadonlyMap<ChannelId, Pref>
  open: ChannelId | null
  can_announce: boolean
  defaults?: ReadonlySet<ChannelId> | undefined
  name_of_dm(c: Channel): string
  others_in(c: Channel): UserId[] | undefined
  self: { user: UserId; name: string }
}) {
  return a.channels
    .filter(
      (c) =>
        !hidden_after_close(a.summaries.get(c.id)?.last ?? c.last, c.closed_upto, c.id === a.open),
    )
    .map((c) => pick_row(c, a))
}

function pick_row(
  c: Channel,
  a: {
    external: ReadonlyMap<ChannelId, string[]>
    summaries: Summaries
    pref_of: ReadonlyMap<ChannelId, Pref>
    can_announce: boolean
    /** 기본 채널들. 모르면 기본 채널 칸을 안 그린다. */
    defaults?: ReadonlySet<ChannelId> | undefined
    name_of_dm(c: Channel): string
    others_in(c: Channel): UserId[] | undefined
    self: { user: UserId; name: string }
  },
) {
  const sum = a.summaries.get(c.id)
  const others = is_dm(c.kind) ? a.others_in(c) : undefined
  const pref = a.pref_of.get(c.id)
  const announce = c.announce ?? false
  return {
    id: c.id,
    name: c.name ?? a.name_of_dm(c),
    unread: sum === undefined ? 0 : Math.max(0, sum.last - sum.read_upto),
    mention_count: sum?.mention_count ?? 0,
    topic: c.topic,
    stored: c.stored,
    announce,
    ...only('external', a.external.get(c.id)),
    ...privacy_of(c, a.can_announce),
    ...(c.kind === 'private' ? { secret: true } : {}),
    ...(a.can_announce && c.kind === 'public' && a.defaults !== undefined
      ? { default_join: a.defaults.has(c.id) }
      : {}),
    locked: announce && !a.can_announce,
    ...(is_dm(c.kind) ? { dm: true, ...only('dm_with', others) } : {}),
    // 나 혼자의 방은 내 얼굴로 그린다 — 방 이름("나와의 대화")의 첫 글자를 그리면 남의 방처럼 보인다(3차 F305).
    ...(others?.length === 0 ? { dm_self: a.self } : {}),
    notify: pref?.notify ?? 'all',
    favorite: pref?.favorite ?? false,
    section: pref?.section ?? null,
  }
}

/** 공개↔비공개 손잡이는 관리자에게만, 일반 채널에만 준다. */
function privacy_of(c: Channel, can_announce: boolean): { private?: boolean } {
  if (!can_announce || (c.kind !== 'public' && c.kind !== 'private')) return {}
  return { private: c.kind === 'private' }
}

/** 내가 들어가거나 나간 채널. 들어가면 세션에 심고, 나가면 뺀다. 목록은 둘 다 다시 받는다. */
function on_my_membership(
  e: Extract<Event, { t: 'membership' }>,
  session: Session,
  api: Session['api'],
  queries: ReturnType<typeof useQueryClient>,
) {
  if (e.joined)
    api
      .channel(e.channel)
      .then(session.adopt)
      .catch(() => {})
  else session.drop(e.channel)
  void queries.invalidateQueries({ queryKey: ['channels'] })
}

/** 부름을 알릴까. 음소거한 채널은 부름도 조용하다 — 여기서 안 막으면 "껐는데 울린다" 가 된다. */
type Whisper = WhisperItem & { channel: ChannelId }

/** 받은 순서 번호. 화면 안에서만 뜻이 있다. */
let whisper_count = 0
/** 나만 보이는 글을 쌓는다. 앱이 쏟아내도 화면을 덮지 않게 최근 20개만. */
function heard(now: readonly Whisper[], e: Extract<Event, { t: 'ephemeral' }>): readonly Whisper[] {
  whisper_count += 1
  return [...now, { key: whisper_count, channel: e.channel, author: e.author, body: e.body }].slice(
    -20,
  )
}

/** 채널 알림 단계. 모르는 값은 가장 시끄러운 쪽이다(AGENTS.md 「알림 단계」). */
function level_in(
  prefs: readonly { channel: ChannelId; notify: string }[] | undefined,
  channel: ChannelId,
): 'all' | 'mentions' | 'none' {
  const level = (prefs ?? []).find((p) => p.channel === channel)?.notify
  return level === 'mentions' || level === 'none' ? level : 'all'
}

function is_dm_kind(kind: string): boolean {
  return kind === 'dm' || kind === 'group_dm'
}

/** 연 채널의 스레드만 열린 것으로 친다 — 채널을 바꾸면 저절로 닫힌다. */
function thread_root_of(at: { channel: ChannelId; root: Seq } | null, open: ChannelId | null) {
  return at !== null && at.channel === open ? at.root : null
}

/**
 * 스레드 판의 답글. 답글도 본류 캐시(`lines`)에 섞여 온다 — 고침·지움 사건은 그쪽을 고치므로, 받아 둔
 * 것(`got`)을 캐시의 새 판으로 갈아 끼우고, 받은 뒤 캐시에 들어온 답글을 순번대로 잇는다. 받기 전(`got`
 * 없음)에는 캐시에 있는 것만 보인다.
 */
export function thread_replies(
  got: readonly Message[] | undefined,
  lines: readonly Message[],
  root: Seq,
): Message[] {
  const fresh = new Map(lines.map((l) => [l.seq, l]))
  const out = (got ?? []).filter((m) => m.seq !== root).map((m) => fresh.get(m.seq) ?? m)
  const have = new Set(out.map((m) => m.seq))
  const late = lines.filter((l) => l.parent === root && !have.has(l.seq))
  return late.length === 0 ? out : [...out, ...late].sort((a, b) => a.seq - b.seq)
}

/** 한 일의 끝을 알림으로 알린다. 실패는 삼키지 않고 까닭을 붙여 보인 뒤 조용히 끝난다. */
async function told(
  toasts: ToastStore,
  work: Promise<unknown>,
  ok: string,
  fail: string,
): Promise<void> {
  try {
    await work
  } catch (e) {
    toasts.show({ title: fail, description: why(e), tone: 'danger' })
    return
  }
  toasts.show({ title: ok, tone: 'ok' })
}

function is_dm(kind: string | undefined): boolean {
  return kind === 'dm' || kind === 'group_dm'
}

/** 값이 있을 때만 그 키를 싣는다(exactOptionalPropertyTypes). */
function only<K extends string, V>(key: K, v: V | undefined): { [P in K]?: V } {
  return (v === undefined ? {} : { [key]: v }) as { [P in K]?: V }
}

/** 호출 알림 한 줄. 사건에는 번호뿐이라 그 줄을 한 번 받는다 — 실패하면 조용히 만다. */
async function announce(
  platform: Platform,
  api: Session['api'],
  channel: ChannelId,
  seq: Seq,
  name_of: (u: UserId) => string | undefined,
  channels: readonly Channel[],
): Promise<void> {
  try {
    const [line] = await api.message_range(channel, seq, seq)
    if (line === undefined) return
    const room = channels.find((c) => c.id === channel)
    const who = name_of(line.author) ?? `#${line.author}`
    // 대화방은 사람이 곧 방이다 — "이하늘 · #대화" 가 아니라 "이하늘".
    const dm = room !== undefined && is_dm_kind(room.kind)
    const where = room?.name ?? translate(locale_now(), msg('대화'))
    await platform.notify({
      subject: dm ? who : `${who} · #${where}`,
      body: text_of(line.body).slice(0, 200),
      // 영구 링크와 같은 해시다. `maru://Channel/…` 을 싣던 때는 앱 어디서도 읽지 않아
      // 알림을 눌러도 아무 데도 가지 않았다.
      deep_link: `#/c/${channel}/${seq}`,
    })
  } catch {
    // 알림은 잃어도 되는 정보다. 배지는 이미 올랐다.
  }
}

/**
 * DM 에는 이름이 없다. 사이드바 목록의 `people`(서버가 한 질의로 싣는다)에서 나를 뺀
 * 이름을 잇는다. 옛 서버라 칸이 없으면 연 방만 그 방의 명단으로 짓고 나머지는 '대화'.
 */
export function dm_name(
  c: Pick<Channel, 'id' | 'people'>,
  open: ChannelId | null,
  here: readonly UserId[] | undefined,
  me: number,
  name_of: (u: UserId) => string | undefined,
  /** 이름 명단을 받았는가. 받기 전엔 모두가 `#번호` 라 새로 고칠 때마다 사이드바가 번호로 번쩍인다. */
  named = true,
): string {
  const people = c.people ?? (c.id === open ? here : undefined)
  if (people === undefined || !named) return translate(locale_now(), msg('대화'))
  const others = people.filter((p) => p !== me).map((p) => name_of(p) ?? `#${p}`)
  return others.length === 0 ? translate(locale_now(), msg('나와의 대화')) : others.join(', ')
}

/** 한 줄에서 열 스레드. 답글(채널에도 보낸 것 포함)이면 그 답글이 달린 원래 스레드다. */
export function thread_of(roots: readonly Message[], seq: Seq): Seq {
  return roots.find((l) => l.seq === seq)?.parent ?? seq
}

/**
 * 본류(루트)와 "루트별 답글 수" 로 가른다. 답글은 본류에 그리지 않는다(슬랙과 같은 규칙) —
 * "채널에도 보내기" 로 쓴 답글만 본류에도 선다. 답글 수에는 그대로 센다.
 */
export function split_replies(lines: readonly Message[]): {
  roots: Message[]
  reply_counts: ReadonlyMap<Seq, number>
  /** 루트별 마지막(지우지 않은) 답글 시각. 답글 단추 옆에 "마지막 답글 5분 전" 으로 선다. */
  reply_last: ReadonlyMap<Seq, string>
} {
  const roots: Message[] = []
  const reply_counts = new Map<Seq, number>()
  const reply_last = new Map<Seq, string>()
  for (const m of lines) {
    if (m.parent === null) {
      roots.push(m)
      continue
    }
    if (m.broadcast === true) roots.push(m)
    if (m.deleted_at !== null) continue
    reply_counts.set(m.parent, (reply_counts.get(m.parent) ?? 0) + 1)
    const last = reply_last.get(m.parent)
    if (last === undefined || Date.parse(m.written_at) >= Date.parse(last))
      reply_last.set(m.parent, m.written_at)
  }
  return { roots, reply_counts, reply_last }
}

/** 서버가 준 표시를 판이 읽는 모양으로. 줄의 생김새는 같고 이름만 다르다. */
/** 이모지와 글을 한 줄로. 상태가 없으면 `null` — 빈 글자를 그리면 빈 칸이 생긴다. */
function say_status(p: ProfileRow | undefined): string | null {
  if (p === undefined) return null
  const said = `${p.status_emoji ?? ''} ${p.status_text ?? ''}`.trim()
  return said === '' ? null : said
}

/** 사이드바 아래 내 자리. 명단을 아직 못 받았으면 자리를 비워 둔다 — 빈 이름을 그리지 않는다. */
function self_of(
  id: UserId,
  name: string | undefined,
  p: ProfileRow | undefined,
): SelfView | undefined {
  return name === undefined
    ? undefined
    : {
        id,
        name,
        status: say_status(p),
        paused_until: p?.snooze_until ?? null,
        away: p?.away === true,
        status_parts: {
          emoji: p?.status_emoji ?? null,
          text: p?.status_text ?? null,
          until: p?.status_until ?? null,
        },
      }
}

/** 전달은 **인용으로 옮겨 적는 일**이다. 서버에 전달이라는 개념은 없다 — 새 글일 뿐이다. */
function quoted(line: Message, title: string, origin: { url: string; view: string }): Block[] {
  const plain = { bold: false, italic: false, strike: false, code: false }
  const head: Block = {
    t: 'para',
    spans: [
      { t: 'text', text: `${title} — `, ...plain },
      // 우리 주소라 카드를 펼칠 것이 없다 — 서버가 헛걸음하지 않게 처음부터 뗀다.
      { t: 'link', url: origin.url, view: origin.view, no_card: true },
    ],
  }
  const body = line.body.map((b): Block => (b.t === 'para' ? { t: 'quote', spans: b.spans } : b))
  return [head, ...body]
}

/** 예약 목록에 보일 한 줄. 서식은 버리고 글자만 — 목록은 본문을 다 보여 줄 자리가 아니다. */
function peek_of(blocks: readonly Block[]): string {
  const text = blocks
    .flatMap((b) => (b.t === 'para' || b.t === 'quote' ? b.spans : []))
    .map((sp) => (sp.t === 'text' ? sp.text : ''))
    .join('')
    .trim()
  return text === '' ? translate(locale_now(), msg('(첨부 파일)')) : text
}

function to_briefs(got: readonly ThreadBrief[]): Brief[] {
  return got.map((b) => ({
    channel: b.channel,
    line: b.root,
    replies: b.replies,
    last_at: b.last_at,
  }))
}

/** 안 읽은 판에 대화마다 몇 줄까지 받는가. 더 많으면 채널에서 본다. */
export const UNREAD_TAKE = 30

interface UnreadTarget {
  channel: ChannelId
  name: string
  count: number
  read_upto: Seq
}

/**
 * 안 읽은 판에 올릴 대화. 사이드바 순서 그대로, 안 읽은 줄이 있고 끄지 않은(`none`) 것만.
 * 슬랙도 음소거한 채널은 Unreads 에 안 올린다.
 */
export function unread_targets(
  picks: readonly { id: ChannelId; name: string; unread: number; notify?: string }[],
  read_upto_of: (id: ChannelId) => Seq | undefined,
): UnreadTarget[] {
  return picks
    .filter((p) => p.unread > 0 && p.notify !== 'none')
    .map((p) => ({
      channel: p.id,
      name: p.name,
      count: p.unread,
      read_upto: read_upto_of(p.id) ?? (0 as Seq),
    }))
}

/** 받아 온 최근 줄에서 읽은 뒤의 것만 남긴다. 지운 줄은 뺀다. */
export function unread_group(t: UnreadTarget, lines: readonly Message[]): UnreadGroup {
  const fresh = lines.filter((l) => l.seq > t.read_upto && l.deleted_at === null)
  return {
    channel: t.channel,
    name: t.name,
    lines: fresh,
    more: Math.max(0, t.count - fresh.length),
  }
}

function to_marks(got: readonly Marked[]): Mark[] {
  return got.map((m) => ({
    channel: m.channel,
    by: m.by,
    line: m.message,
    ...(m.later === undefined ? {} : { later: m.later }),
  }))
}

/** 이 사건이면 찾아보기 목록을 다시 받는다 — 공개 채널이 비공개가 됐다. */
export function hides_from_browse(e: Event): boolean {
  return e.t === 'kind_changed' && e.private
}

/** 찾아보기 안내. 목록을 띄운 사이 채널이 비공개가 되면 들어가기가 404 다. */
export const NOW_PRIVATE = msg('이 채널은 비공개로 바뀌어 참여할 수 없어요. 목록을 새로 고쳤어요.')

/**
 * 찾아보기에서 들어간다. 404 면 그 사이 비공개가 된 것이다 — 목록을 다시 받게 하고
 * 그 까닭을 사람 말로 던진다. 다른 실패는 그대로 올린다.
 */
export async function join_or_explain(join: () => Promise<unknown>, refresh: () => void) {
  try {
    await join()
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      refresh()
      throw new Error(translate(locale_now(), NOW_PRIVATE))
    }
    throw err
  }
}

/** 연 채널에서 쓰는 사람들(나 빼고)을 쉼표로 이은 글자. 같은 명단이면 같은 글자다. */
function typing_line(
  typing: ReadonlyMap<ChannelId, ReadonlySet<number>>,
  open: ChannelId | null,
  me: number,
): string {
  return open === null ? '' : [...(typing.get(open) ?? [])].filter((w) => w !== me).join(',')
}
